import { Geometry2, prepareGeometry } from "@omnia/shared/geom"
import { createCroppingStyleProvider, DateString, ICrop, IField, IOmniaContext, IOmniaHttpService, OmniaApiInterface, readGeometriesFromOmniaBinaryToIndex, SharedDataService } from "../omnia"
import { setupFieldsFromChanges } from "./changes"
import { setupFields } from "./init"
import { GeometryFormatId, hasBinaryResponses } from "@omnia/shared/geom/binary"

export class FieldsLoader {

    private _context: IOmniaContext

    private _crops: ICrop[]

    private _fields: IField[]
    private _fieldsTimestamp: DateString

    constructor(
        private $q: ng.IQService,
        private ohttp: IOmniaHttpService,
        private api: OmniaApiInterface,
        private sharedData: SharedDataService,
        private readonly geometryFormat: GeometryFormatId) {
    }


    get fields(): IField[] {
        return this._fields || []
    }

    load(context: IOmniaContext, reload: boolean): ng.IPromise<any> {

        this._context = context || <IOmniaContext>{};

        const holdingId = this._context.holdingId

        if (!holdingId) {
            return this.$q.resolve([])
        }

        if (!this._crops) {
            reload = true
        }

        const { _fields: fields, _fieldsTimestamp: timestamp } = this

        const queries = {

            shared: this.sharedData.refresh(),

            holding: timestamp
                ? this.getHoldingFieldsIfModified(fields, timestamp)
                : this.getHoldingFields(holdingId),

        }

        return this.$q.all(queries)
            .then(r => {

                this._crops = this.sharedData.crops
                this._fields = this.setupFields(r.holding.fields)
                this._fieldsTimestamp = r.holding.timestamp ?? new Date().toISOString()

            })

    }

    loadFields(fieldIds: number[]): ng.IPromise<any> {

        return this.getFields(fieldIds).then(updated => {

            const fields = this._fields || []

            this._fields = fields
                .map(f => updated.find(i => i.FieldId == f.FieldId) || f)
                .concat(updated.filter(u => fields.every(i => i.FieldId != u.FieldId)))

            return updated

        })

    }

    loadCroppingPermissions(year: number) {

        interface CroppingPermission {
            CroppingId: number
            Authorized: boolean
        }

        const url = `/api/fields/v1/cropping/permissions?year=${year}`

        return this.ohttp.get<CroppingPermission[]>(url, false).then(r => r.data ?? [])

    }

    private getFields(fieldIds: number[]): ng.IPromise<IField[]> {

        const url = `/api/fields/get/fields/?holdingId=${this._context.holdingId}&fieldIds=${fieldIds.join(",")}`

        return this.ohttp.get(url).then(r => {
            return this.setupFields(r.data)
        })

    }


    private getHoldingFields(holdingId: number): ng.IPromise<{ fields: IField[], timestamp?: DateString }> {

        const { geometryFormat: format } = this

        return hasBinaryResponses(format)
            ? this.getHoldingFieldsAsBinary(holdingId, format)
            : this.getHoldingFieldsAsText(holdingId, format)

    }


    private getHoldingFieldsAsText(holdingId: number, format: GeometryFormatId): ng.IPromise<{ fields: IField[], timestamp?: DateString }> {

        const url = `/api/fields/v4/fields/holding/${holdingId}?geometryFormat=${format}`

        type FieldWithBoundaryId = Omit<IField, "Boundary"> & {
            Boundary: number
        }

        return this.ohttp.get(url).then(r => {

            const data = r.data

            const geometries = setupGeometryIndex(data.Geometries || {})

            const fields = setupGeometries(data.Fields as FieldWithBoundaryId[], geometries)

            for (const f of fields) {
                f.Cropping = setupGeometries(f.Cropping as any, geometries) as any
            }

            return {
                fields,
                timestamp: data.Timestamp
            }

        })

    }

    private getHoldingFieldsAsBinary(holdingId: number, format: GeometryFormatId): ng.IPromise<{ fields: IField[], timestamp?: DateString }> {

        const url = `/api/fields/v4/fields/holding/${holdingId}?geometryFormat=${format}`

        return this.ohttp.getArrayBuffer(url).then(buffer => {

            const data = readGeometriesFromOmniaBinaryToIndex<{ Fields: any[], Timestamp: string, Geometries: { [key: number]: Geometry2 } }>(buffer)

            const fields: any = setupGeometries(data.Fields, data.Geometries)

            for (const f of fields) {
                f.Cropping = setupGeometries(f.Cropping, data.Geometries)
            }

            return {
                fields,
                timestamp: data.Timestamp
            }

        })

    }


    private getHoldingFieldsIfModified(current: IField[], timestamp: DateString): ng.IPromise<{ fields: IField[], timestamp?: DateString }> {

        current = current ?? []

        let url = `/api/fields/v1/fields/changes`

        if (timestamp) {
            url += "?since=" + encodeURIComponent(timestamp)
        }

        return this.ohttp.get(url).then(r => {

            const data = r.data

            const geometries = data.Geometries || {}
            data.Fields.Resources = setupGeometries(data.Fields.Resources, geometries)
            data.Cropping.Resources = setupGeometries(data.Cropping.Resources, geometries)

            const partial = Boolean(timestamp)

            const fields = setupFieldsFromChanges(current, data, partial)

            return { fields, timestamp: data.Timestamp }

        })

    }

    private setupFields(fields: IField[]) {

        const styleProvider = createCroppingStyleProvider(this.sharedData.cropStyles)

        return setupFields(fields, this.sharedData, styleProvider)

    }

    deleteField(fieldId: number, admin: boolean): ng.IPromise<any> {

        const url = `/api/fields/delete/field/${fieldId}` + (admin ? "?dependencies=true" : "")

        return this.api.delete(url, undefined, false).then(r => {
            this._fields.remove(f => f.FieldId == fieldId)
            return r
        })

    }

}

function setupGeometryIndex(index: { [key: number]: Geometry2 }): { [key: number]: Geometry2 } {

    return Object.entries(index).reduce((i, [key, value]) => {

        i[key] = prepareGeometry(value)

        return i

    }, {} as { [key: number]: Geometry2 })

}


export function setupGeometries<T extends { Boundary: number }>(items: T[], index: { [key: number]: Geometry2 }): (Omit<T, "Boundary"> & { Boundary: Geometry2 })[] {

    return items.map(i => ({
        ...i,
        Boundary: index[i.Boundary]
    }))

}
